import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../services/api';

// ── Helpers ───────────────────────────────────────────────────────────────────
const btn = (bg, color = '#fff', extra = {}) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color,
  border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13,
  fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'opacity 0.15s', ...extra,
});

const AlertBox = ({ variant, message, onClose }) => {
  const map = {
    danger:  { bg: '#fff1f2', border: '#fecdd3', color: '#9f1239' },
    warning: { bg: '#fffbeb', border: '#fde68a', color: '#92400e' },
    success: { bg: '#f0fdf4', border: '#bbf7d0', color: '#065f46' },
    info:    { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af' },
  };
  const s = map[variant] || map.info;
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: s.color, marginBottom: 14 }}>
      <span>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: s.color, fontSize: 16, lineHeight: 1, padding: '0 0 0 12px' }}>×</button>
    </div>
  );
};

const ExcelUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fetchedData, setFetchedData] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [yearFilter, setYearFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'danger' });
  const navigate = useNavigate();

  useEffect(() => { fetchUploadedFiles(); }, []);

  const fetchUploadedFiles = async () => {
    try {
      const response = await api.get('data/getPrDetails/');
      setFetchedData(response.data);
    } catch (error) {
      console.error('Error fetching file list:', error);
      setAlert({ show: true, message: 'Failed to load previously uploaded files.', variant: 'danger' });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setAlert({ show: true, message: 'Please select a file to upload.', variant: 'warning' });
      return;
    }
    setIsLoading(true);
    setAlert({ show: false });
    try {
      const jsonData = await prepareFileData(selectedFile);
      const response = await api.post('data/', jsonData);
      if (response.status === 201) {
        setAlert({ show: true, message: 'File uploaded successfully!', variant: 'success' });
        fetchUploadedFiles();
        setSelectedFile(null);
        const fi = document.querySelector('input[type="file"]');
        if (fi) fi.value = '';
      }
    } catch (error) {
      console.error('Full upload error response:', error.response);
      let msg = 'An unexpected error occurred during upload.';
      if (error.response?.data) {
        const d = error.response.data;
        if (typeof d === 'object') {
          msg = Object.entries(d).map(([f, m]) => `${f}: ${m.join(', ')}`).join('; ');
        } else if (d.error) {
          msg = d.error;
        }
      }
      setAlert({ show: true, message: `Upload Failed: ${msg}`, variant: 'danger' });
    } finally {
      setIsLoading(false);
    }
  };

  const prepareFileData = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const binaryContent = event.target.result;
        const workbook = XLSX.read(binaryContent, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        if (!data || data.length === 0 || !data[0]) return reject(new Error('Invalid file format.'));
        const columns = data[0];
        const file_data = data.slice(1).map((row) => {
          const obj = {};
          columns.forEach((col, i) => { obj[col] = row[i]; });
          return obj;
        });
        resolve({ filename: file.name, file_data });
      } catch (e) {
        reject(new Error('Error processing file: ' + e.message));
      }
    };
    reader.onerror = () => reject(new Error('Error reading the file.'));
    reader.readAsBinaryString(file);
  });

  const toggleCheckboxes = () => { setShowCheckboxes(!showCheckboxes); setSelectedItems({}); };

  const handleDownloadSelected = async () => {
    const filenames = Object.keys(selectedItems);
    if (filenames.length === 0) {
      setAlert({ show: true, message: 'Please select at least one file to download.', variant: 'warning' });
      return;
    }
    setIsLoading(true);
    setAlert({ show: false });
    try {
      const response = await api.post('data/download_data/', { filenames }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'Norms_downloaded_data.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setAlert({ show: true, message: 'Your file has started downloading.', variant: 'info' });
    } catch (error) {
      console.error('Download error:', error);
      setAlert({ show: true, message: 'There was a problem downloading your file.', variant: 'danger' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewSelected = async () => {
    const selectedFilenames = Object.keys(selectedItems);
    if (selectedFilenames.length !== 1) {
      setAlert({ show: true, message: 'Please select exactly one file to view.', variant: 'warning' });
      return;
    }
    const filename = selectedFilenames[0];
    try {
      const response = await api.get(`data/getsummary/?fName=${filename}`);
      navigate('/summary', { state: { summaryData: response.data } });
    } catch (error) {
      console.error('Error fetching summary data:', error);
      setAlert({ show: true, message: `Error fetching data for ${filename}.`, variant: 'danger' });
    }
  };

  const handleDeleteSelected = async () => {
    const filenamesToDelete = Object.keys(selectedItems);
    if (filenamesToDelete.length === 0) {
      setAlert({ show: true, message: 'Please select files to delete.', variant: 'warning' });
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${filenamesToDelete.length} file(s)? This action cannot be undone.`)) return;
    setIsLoading(true);
    setAlert({ show: false });
    try {
      const response = await api.post('data/delete_projects/', { filenames: filenamesToDelete });
      setAlert({ show: true, message: response.data.message || 'Selected files deleted successfully.', variant: 'success' });
    } catch (error) {
      console.error('Error deleting files:', error);
      setAlert({ show: true, message: `Deletion Failed: ${error.response?.data?.error || 'Unexpected error.'}`, variant: 'danger' });
    } finally {
      setIsLoading(false);
      setSelectedItems({});
      fetchUploadedFiles();
    }
  };

  const handleCheckboxChange = (filename, isChecked) => {
    setSelectedItems((prev) => {
      const next = { ...prev };
      if (isChecked) next[filename] = true;
      else delete next[filename];
      return next;
    });
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      const all = {};
      filteredData.forEach((f) => { all[f.filename] = true; });
      setSelectedItems(all);
    } else {
      setSelectedItems({});
    }
  };

  const filteredData = yearFilter.trim() === ''
    ? fetchedData
    : fetchedData.filter((f) => f.filename?.toLowerCase().includes(yearFilter.trim().toLowerCase()));

  const selectedCount = Object.keys(selectedItems).length;

  return (
    <div style={{ padding: '24px 28px', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ background: '#0f172a', borderRadius: 12, padding: '18px 24px', marginBottom: 24 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: '#fff' }}>Data Upload</div>
        <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 2 }}>Upload and manage project data files</div>
      </div>

      {alert.show && <AlertBox variant={alert.variant} message={alert.message} onClose={() => setAlert({ show: false })} />}

      {/* Upload card */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 20px', marginBottom: 16 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Upload File</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => setSelectedFile(e.target.files[0])}
            style={{ flex: 1, minWidth: 220, padding: '7px 10px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 13, color: '#334155', fontFamily: 'inherit', cursor: 'pointer' }}
          />
          <button onClick={handleUpload} disabled={isLoading} style={btn('#0f172a', '#fff', { opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' })}>
            {isLoading ? '⏳ Uploading…' : '↑ Upload'}
          </button>
        </div>
      </div>

      {/* File list card */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 20px' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={toggleCheckboxes} style={btn(showCheckboxes ? '#475569' : '#f1f5f9', showCheckboxes ? '#fff' : '#334155')}>
              {showCheckboxes ? '✕ Cancel' : '☑ Select Files'}
            </button>
            {showCheckboxes && (
              <>
                <button onClick={handleDownloadSelected} disabled={selectedCount === 0} style={btn('#16a34a', '#fff', { opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? 'not-allowed' : 'pointer' })}>
                  ↓ Download
                </button>
                <button onClick={handleViewSelected} disabled={selectedCount !== 1} style={btn('#3b82f6', '#fff', { opacity: selectedCount !== 1 ? 0.5 : 1, cursor: selectedCount !== 1 ? 'not-allowed' : 'pointer' })}>
                  ◉ View
                </button>
                <button onClick={handleDeleteSelected} disabled={selectedCount === 0} style={btn('#f43f5e', '#fff', { opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? 'not-allowed' : 'pointer' })}>
                  ✕ Delete
                </button>
              </>
            )}
          </div>

          {/* Search */}
          <div style={{ position: 'relative', width: 260 }}>
            <input
              type="text"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              placeholder="Search by filename…"
              style={{ width: '100%', padding: '7px 32px 7px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
            {yearFilter && (
              <button onClick={() => setYearFilter('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
            )}
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {showCheckboxes && (
                  <th style={{ padding: '10px 14px', width: 40 }}>
                    <input
                      type="checkbox"
                      onChange={toggleSelectAll}
                      checked={filteredData.length > 0 && selectedCount === filteredData.length}
                      disabled={filteredData.length === 0}
                      style={{ accentColor: '#0f172a' }}
                    />
                  </th>
                )}
                {['S.No', 'File Name', 'Sample Size', 'Uploaded By'].map((h) => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11.5, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? filteredData.map((file, index) => (
                <tr key={file.filename || index} style={{ borderBottom: '1px solid #f1f5f9', background: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                  {showCheckboxes && (
                    <td style={{ padding: '9px 14px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!selectedItems[file.filename]}
                        onChange={(e) => handleCheckboxChange(file.filename, e.target.checked)}
                        style={{ accentColor: '#0f172a' }}
                      />
                    </td>
                  )}
                  <td style={{ padding: '9px 14px', color: '#64748b', width: 60 }}>{index + 1}</td>
                  <td style={{ padding: '9px 14px', color: '#0f172a', fontWeight: 500 }}>{file.filename}</td>
                  <td style={{ padding: '9px 14px', color: '#475569', textAlign: 'center' }}>{file.record_count}</td>
                  <td style={{ padding: '9px 14px', color: '#475569' }}>{file.uploaded_by}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={showCheckboxes ? 5 : 4} style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                    No files have been uploaded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExcelUpload;
